import type { AiProfile } from "@/types/ai";
import { sendVoicePilotMessage, type VoicePilotContext } from "./voicePilot";
import { executeVoicePilotActions } from "./voicePilotActions";
import { eventBus } from "@/lib/utils/eventBus";
import { getUserId } from "@/lib/user/identity";
import { tokenControl } from "@/lib/consciousness/tokenControl";

// Runs a multi-step plan produced by /api/karma-ac/voice/plan. Deliberately
// decoupled from the voice turn's own mic/TTS state machine (see
// components/karma-ac/MindalooShell.tsx handleVoicePilot) — that reentrancy
// logic was hard-won and fragile, and a multi-step background task shouldn't
// have to fight it for control of when the mic can restart. Each step reuses
// the same tested single-turn pilot pipeline (sendVoicePilotMessage +
// executeVoicePilotActions) rather than duplicating that logic.
//
// Tile-scoped and concurrent: a plan can be launched against a specific tile
// (tileId/tileName below). All the state that matters per plan — the step
// history fed to each subsequent step — lives in this function's own local
// closure, not anywhere shared, so two plans running against two different
// tiles never see each other's state; they're just two ordinary concurrent
// async calls. runningTiles below only guards against the one real hazard:
// launching a second plan on the SAME tile while one is already running,
// which would race on that tile's own eventual UI state.
//
// Progress currently surfaces as toasts (prefixed with the tile name when
// scoped to one) rather than inline inside the tile itself — a real, richer
// in-tile progress view is the natural next increment, not built here.

const MIN_TOKENS_TO_CONTINUE = 300; // a step realistically costs at least this much

const runningTiles = new Set<string>();

export interface RunPlannedTaskOptions {
  tileId?: string;
  tileName?: string;
}

export async function runPlannedTask(
  goal: string,
  profile: AiProfile,
  context: VoicePilotContext,
  options: RunPlannedTaskOptions = {}
): Promise<void> {
  const { tileId, tileName } = options;
  const label = tileName ? `${tileName} — ` : "";

  if (tileId) {
    if (runningTiles.has(tileId)) {
      eventBus.emit("TOAST:SHOW", { message: `${label}already working on something — wait for it to finish.`, type: "error" });
      return;
    }
    runningTiles.add(tileId);
  }

  try {
    const userId = getUserId();

    let steps: string[];
    try {
      const res = await fetch("/api/karma-ac/voice/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile, goal, userId }),
      });
      const data = await res.json();
      if (!res.ok) {
        eventBus.emit("TOAST:SHOW", { message: `${label}couldn't plan that: ${data.error ?? "unknown error"}`, type: "error" });
        return;
      }
      steps = data.steps;
    } catch {
      eventBus.emit("TOAST:SHOW", { message: `${label}planning failed to start.`, type: "error" });
      return;
    }

    eventBus.emit("TOAST:SHOW", { message: `${label}plan: ${steps.length} step${steps.length === 1 ? "" : "s"}.`, type: "info" });

    const history: Array<{ role: "user" | "assistant"; content: string }> = [];

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];

      // Real budget check between steps, not just at plan time — a long
      // conversation history across steps means later steps cost more than
      // the plan's own upfront estimate accounted for. Shared across
      // concurrently-running tiles on purpose: it's one account's real
      // budget regardless of how many tiles are drawing on it at once.
      const budget = await tokenControl.refresh(true);
      if (budget.dailyRemaining < MIN_TOKENS_TO_CONTINUE) {
        eventBus.emit("TOAST:SHOW", {
          message: `${label}stopped after step ${i}/${steps.length} — daily budget reached.`,
          type: "error",
        });
        return;
      }

      eventBus.emit("TOAST:SHOW", { message: `${label}step ${i + 1}/${steps.length}: ${step}`, type: "info", duration: 4000 });

      try {
        const result = await sendVoicePilotMessage(step, profile, context, history);
        history.push({ role: "user", content: step });
        history.push({ role: "assistant", content: result.message });

        if (result.actions.length > 0) {
          const outcomes = executeVoicePilotActions(result.actions);
          const failures = outcomes.filter((o) => !o.success);
          if (failures.length > 0) {
            eventBus.emit("TOAST:SHOW", {
              message: `${label}step ${i + 1} had an issue: ${failures.map((f) => f.message).join("; ")}`,
              type: "error",
            });
          }
        }
      } catch (error: any) {
        eventBus.emit("TOAST:SHOW", {
          message: `${label}stopped at step ${i + 1}/${steps.length}: ${error?.message ?? "step failed"}`,
          type: "error",
        });
        return;
      }
    }

    eventBus.emit("TOAST:SHOW", { message: `${label}done — completed all ${steps.length} steps.`, type: "success" });
  } finally {
    if (tileId) runningTiles.delete(tileId);
  }
}
